<script setup>
import { formatTelLink } from '@/composables/useFormatTelLink';
import { useGlobalStore } from '@/stores/global';
import FormSubscription from '@/components/forms/FormSubscription.vue';
defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const globalStore = useGlobalStore();
</script>
<template>
  <section
    :id="data.id"
    class="section white"
    :class="[
      data?.spacing?.top ? `section--top-${data.spacing.top}` : '',
      data?.spacing?.bottom ? `section--bottom-${data.spacing.bottom}` : '',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div
      class="application__background"
      :style="{
        'background-image': 'url(' + data?.background_image?.src + ')',
      }"
      :alt="data?.background_image?.alt"
    ></div>
    <div class="container">
      <div class="application">
        <div class="application__content">
          <h2
            v-if="data.title && data.title.length > 0"
            class="mb-0 application__title display"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            v-html="data.title"
          ></h2>
          <div
            class="application__form"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
          >
            <FormSubscription>
              <p
                v-if="globalStore.termsLink || globalStore.policyLink"
                class="mb-0 application__form-text body-3-regular"
              >
                {{ $t('application.agreement_start') }}
                {{ $t('subscription.send_button')
                }}{{ $t('application.agreement_middle') }}
                <CTagDetect
                  v-if="globalStore.termsLink"
                  :to="globalStore.termsLink"
                  class="text-decoration-underline"
                >
                  {{ $t('application.agreement_terms_of_use') }}
                </CTagDetect>
                <template v-else>
                  {{ $t('application.agreement_terms_of_use') }}
                </template>
                {{ $t('application.agreement_between') }}
                <CTagDetect
                  v-if="globalStore.policyLink"
                  :to="globalStore.policyLink"
                  class="text-decoration-underline"
                >
                  {{ $t('application.agreement_privacy_policy') }}
                </CTagDetect>
                <template v-else>
                  {{ $t('application.agreement_privacy_policy') }}
                </template>
              </p>
            </FormSubscription>
          </div>
          <div
            v-if="data?.contacts"
            class="application__contacts"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
          >
            <div class="application__contacts-top">
              <p
                v-if="data?.contacts?.title"
                class="mb-0 application__contacts-title body-3-regular"
                v-html="data?.contacts?.title"
              ></p>
              <a
                :href="`tel:${formatTelLink(data?.contacts?.phone)}`"
                class="application__contacts-phone h4"
              >
                <CIcon
                  class="application__contacts-phone-icon"
                  name="phone-red"
                  width="24"
                  height="24"
                />{{ data?.contacts?.phone }}</a
              >
            </div>
            <p
              v-if="data?.contacts?.text"
              class="mb-0 application__contacts-text body-3-regular"
              v-html="data?.contacts?.text"
            ></p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.application {
  position: relative;
  @include media-breakpoint-up(sm) {
    padding: 0 60px;
  }
  @include media-breakpoint-up(md) {
    padding: 0;
  }
  &__background {
    background: no-repeat center center/cover;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    height: calc(100% + 160px);
    left: -16px;
    overflow: hidden;
    position: absolute;
    top: -80px;
    width: calc(100% + 32px);
    z-index: 0;
    &::before {
      background-color: rgba($black, 0.9);
      content: '';
      height: 100%;
      left: 0;
      top: 0;
      position: absolute;
      width: 100%;
      z-index: 1;
    }
    @include media-breakpoint-up(sm) {
      height: calc(100% + 240px);
      left: -20px;
      top: -120px;
      width: calc(100% + 40px);
    }
    @include media-breakpoint-up(md) {
      left: -48px;
      width: calc(100% + 96px);
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(left, -48);
      @include responsive-spacing(top, -120);
      height: calc(100% + px-to-vw(240));
      width: calc(100% + px-to-vw(96));
    }
  }
  &__content {
    display: flex;
    flex-direction: column;
    gap: 56px;
    position: relative;
    z-index: 2;
    @include media-breakpoint-up(sm) {
      gap: 80px;
    }
    @include media-breakpoint-up(md) {
      align-items: start;
      display: grid;
      gap: 48px;
      grid-template-columns: repeat(2, 6fr);
      grid-template-areas: 'title form' 'contacts form';
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
  }
  &__title {
    grid-area: title;
    margin: 0;
    text-align: center;
    @include media-breakpoint-up(md) {
      text-align: left;
    }
  }
  &__form {
    display: flex;
    grid-area: form;
    flex-direction: column;
    gap: 24px;
    @include media-breakpoint-up(sm) {
      gap: 32px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 32);
    }
    &-text {
      padding-top: 24px;
      text-align: center;
      @include media-breakpoint-up(sm) {
        padding-top: 32px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-top, 32);
      }
      @include media-breakpoint-up(md) {
        text-align: left;
      }
    }
  }
  &__contacts {
    display: flex;
    gap: 24px;
    grid-area: contacts;
    flex-direction: column;
    text-align: center;
    @include media-breakpoint-up(md) {
      gap: 32px;
      text-align: left;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 32);
    }
    &-top {
      display: flex;
      flex-direction: column;
      gap: 8px;
      @include media-breakpoint-up(md) {
        gap: 16px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 16);
      }
    }
    &-phone {
      align-items: center;
      align-self: center;
      display: grid;
      gap: 16px;
      grid-template-columns: 24px auto;
      max-width: fit-content;
      @include media-breakpoint-up(md) {
        align-self: self-start;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 16);
        grid-template-columns: px-to-vw(24) auto;
      }
      &-icon {
        height: 100%;
        width: 100%;
      }
    }
    &-text {
      @include media-breakpoint-up(md) {
        max-width: 49%;
      }
    }
  }
}
</style>
